import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Company, CreateCompanyRequest, UpdateCompanyRequest } from '../types';
import { apiClient } from '../services/api';
import { PaginatedSnapshot, restorePaginatedQueries, snapshotPaginatedQueries, updatePaginatedQueries } from '../utils/queryCache';

type SaveCompanyInput = { id?: string; data: CreateCompanyRequest | UpdateCompanyRequest };
type CompanyMutationContext = { snapshots: PaginatedSnapshot<Company> };

export const useCompanyMutations = () => {
  const queryClient = useQueryClient();
  const invalidateCompanies = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['companies'] }),
      queryClient.invalidateQueries({ queryKey: ['admin'] }),
    ]);
  };

  const saveCompanyMutation = useMutation<Company, unknown, SaveCompanyInput, CompanyMutationContext | undefined>({
    mutationFn: ({ id, data }) => id
      ? apiClient.updateCompany(id, data as UpdateCompanyRequest)
      : apiClient.createCompany(data as CreateCompanyRequest),
    onMutate: async ({ id, data }) => {
      if (!id) return undefined;
      await queryClient.cancelQueries({ queryKey: ['companies'] });
      const snapshots = snapshotPaginatedQueries<Company>(queryClient, 'companies');
      updatePaginatedQueries<Company>(queryClient, 'companies', page => ({
        ...page,
        content: page.content.map(company => company.id === id ? { ...company, ...data } : company),
      }));
      return { snapshots };
    },
    onError: (_error, _input, context) => {
      if (context) restorePaginatedQueries(queryClient, context.snapshots);
    },
    onSettled: invalidateCompanies,
  });

  const deleteCompanyMutation = useMutation<void, unknown, string, CompanyMutationContext>({
    mutationFn: id => apiClient.deleteCompany(id),
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey: ['companies'] });
      const snapshots = snapshotPaginatedQueries<Company>(queryClient, 'companies');
      updatePaginatedQueries<Company>(queryClient, 'companies', page => ({
        ...page,
        content: page.content.filter(company => company.id !== id),
        totalElements: Math.max(0, page.totalElements - 1),
      }));
      return { snapshots };
    },
    onError: (_error, _id, context) => {
      if (context) restorePaginatedQueries(queryClient, context.snapshots);
    },
    onSettled: invalidateCompanies,
  });

  return { saveCompanyMutation, deleteCompanyMutation };
};
